import { ChannelIdentity, CommandType, WebsocketClient } from './websocket_client';
import { EventBus } from './websocket_bus';

type GetClient = () => WebsocketClient | null | undefined;
type GetUserId = () => number | null | undefined;

interface RemoteEvalPayload {
    type?: string;
    session_id?: string;
    request_id?: string;
    code?: string;
}

interface RemoteEvalMessage {
    Data?: RemoteEvalPayload;
    Channel?: ChannelIdentity;
}

const LOG_RATE_LIMIT_PER_SEC = 20;

let installed = false;
let sessionActive = false;
let activeSessionId: string | null = null;
let getClient: GetClient = () => null;
let getUserId: GetUserId = () => null;
let bus: EventBus | null = null;

let originalError: typeof console.error | null = null;
let originalWarn: typeof console.warn | null = null;
let errorHandler: OnErrorEventHandler | null = null;
let rejectionHandler: ((ev: PromiseRejectionEvent) => void) | null = null;
let logTokens = LOG_RATE_LIMIT_PER_SEC;
let logLastRefill = Date.now();

function refillLogTokens() {
    const now = Date.now();
    const elapsed = now - logLastRefill;
    if (elapsed >= 1000) {
        logTokens = LOG_RATE_LIMIT_PER_SEC;
        logLastRefill = now;
    }
}

function allowLogSend(): boolean {
    refillLogTokens();
    if (logTokens <= 0) {
        return false;
    }
    logTokens -= 1;
    return true;
}

function safeArg(value: any): any {
    if (value == null) {
        return value;
    }
    if (value instanceof Error) {
        return { message: value.message, stack: value.stack };
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }
    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        try {
            return String(value);
        } catch {
            return '[unserializable]';
        }
    }
}

function serializeResult(value: any): any {
    if (value === undefined) {
        return null;
    }
    return safeArg(value);
}

function sendRemoteMessage(data: Record<string, any>) {
    const client = getClient();
    const userId = getUserId();
    if (!client || !(userId > 0)) {
        return;
    }
    client.sendCommand({
        Command: CommandType.Message,
        Channel: { Type: 'remoteEval', ID: String(userId) },
        Data: data,
    });
}

function pushLog(level: string, args: any[], stack?: string) {
    if (!sessionActive || !allowLogSend()) {
        return;
    }
    sendRemoteMessage({
        type: 'log',
        level,
        t: Date.now(),
        args: args.map(safeArg),
        stack: stack || undefined,
    });
}

function armConsoleHooks() {
    if (originalError) {
        return;
    }
    originalError = console.error.bind(console);
    originalWarn = console.warn.bind(console);

    console.error = (...args: any[]) => {
        originalError!(...args);
        pushLog('error', args);
    };
    console.warn = (...args: any[]) => {
        originalWarn!(...args);
        pushLog('warn', args);
    };

    errorHandler = ((event: ErrorEvent) => {
        pushLog('error', [event.message], event.error?.stack);
    }) as OnErrorEventHandler;
    rejectionHandler = (event: PromiseRejectionEvent) => {
        const reason = event.reason;
        const stack = reason instanceof Error ? reason.stack : undefined;
        pushLog('error', [reason], stack);
    };
    window.addEventListener('error', errorHandler as any);
    window.addEventListener('unhandledrejection', rejectionHandler);
}

function disarmConsoleHooks() {
    if (originalError) {
        console.error = originalError;
        originalError = null;
    }
    if (originalWarn) {
        console.warn = originalWarn;
        originalWarn = null;
    }
    if (errorHandler) {
        window.removeEventListener('error', errorHandler as any);
        errorHandler = null;
    }
    if (rejectionHandler) {
        window.removeEventListener('unhandledrejection', rejectionHandler);
        rejectionHandler = null;
    }
}

function channelMatchesCurrentUser(channel?: ChannelIdentity): boolean {
    const userId = getUserId();
    if (!(userId > 0) || !channel || channel.Type !== 'remoteEval') {
        return false;
    }
    return String(channel.ID) === String(userId);
}

function attach(sessionId?: string) {
    sessionActive = true;
    activeSessionId = sessionId || null;
    armConsoleHooks();
}

function detach(sessionId?: string) {
    // Stale detach from an older session must not disarm a newer attach.
    if (sessionId && activeSessionId && sessionId !== activeSessionId) {
        return;
    }
    sessionActive = false;
    activeSessionId = null;
    disarmConsoleHooks();
}

function runEval(payload: RemoteEvalPayload) {
    // Only accept eval after an explicit attach for this tab.
    if (!sessionActive) {
        return;
    }
    if (payload.session_id && activeSessionId && payload.session_id !== activeSessionId) {
        return;
    }
    const requestId = payload.request_id;
    if (!requestId) {
        return;
    }
    const reply = (ok: boolean, result: any, error: string | null) => {
        sendRemoteMessage({
            type: 'eval_result',
            request_id: requestId,
            ok,
            result: ok ? serializeResult(result) : null,
            error,
        });
    };
    try {
        // Indirect eval runs in global scope; await thenables (e.g. lag probe).
        const result = (0, eval)(payload.code || '');
        if (result && typeof result.then === 'function') {
            Promise.resolve(result).then(
                (value) => reply(true, value, null),
                (err) => reply(false, null, err instanceof Error ? err.message : String(err)),
            );
            return;
        }
        reply(true, result, null);
    } catch (err) {
        reply(false, null, err instanceof Error ? err.message : String(err));
    }
}

function onBroadcast(message: RemoteEvalMessage) {
    if (!channelMatchesCurrentUser(message?.Channel)) {
        return;
    }
    const data = message?.Data;
    if (!data || typeof data !== 'object') {
        return;
    }
    switch (data.type) {
        case 'attach':
            attach(data.session_id);
            break;
        case 'detach':
            detach(data.session_id);
            break;
        case 'eval':
            runEval(data);
            break;
    }
}

export function initRemoteEval(options: { getClient: GetClient; getUserId: GetUserId; bus?: EventBus }) {
    if (installed) {
        return;
    }
    installed = true;
    getClient = options.getClient;
    getUserId = options.getUserId;
    bus = options.bus || getClient()?.eventBus() || null;
    if (!bus) {
        return;
    }
    bus.subscribe({ type: 'broadcast', channel: { ID: null, Type: 'remoteEval' } }, onBroadcast);
}
